import type { Book, Chapter, ChapterComment, PendingConflict } from "./models";
import { withSpineOrderAndNumbers } from "./bookSpine";
import { mergeChapterVariations } from "./mapLegacyVariations";
import { toChapterDoc, serializeChapterContent } from "./chapterContent";
import {
  applyConflictResolutions,
  threeWayChapterMerge,
  type ChapterConflictResolution,
  type ThreeWayMergeResult,
} from "./chapterSync";
import type { ChapterDocContent } from "./chapterParagraphs";

/**
 * Bidirectional merge for desktop-v2 books: 3-way body merge when both sides diverge;
 * otherwise newer `updatedAt` wins for metadata. Delete rules match legacy dbSyncService.
 */

export type ChapterTimestamps = Record<string, string>;

export type SyncPushResult = {
  syncedChapters: Array<{ order: number; title: string }>;
  failedChapters: Array<{ order: number; title: string; message: string }>;
  chaptersInDatabase?: number;
  chapterRevs?: Record<string, number>;
};

export type DesktopV2SyncAdapter = {
  bookExists(bookId: string): Promise<boolean>;
  getBookUpdatedAt(bookId: string): Promise<string | null>;
  getChapterTimestamps(bookId: string): Promise<ChapterTimestamps>;
  loadRemoteBook(bookId: string): Promise<Book | null>;
  pushBook(book: Book): Promise<SyncPushResult>;
};

function chapterTimeMs(iso: string): number {
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function chapterFingerprint(ch: Chapter): string {
  return `${ch.updatedAt}\0${ch.title}\0${ch.caption ?? ""}\0${ch.order}\0${ch.content}`;
}

/** Body-only fingerprint — ignores `updatedAt` so comment/timestamp touches don't look like edits. */
function chapterBodyFingerprint(ch: Chapter): string {
  return `${ch.title}\0${ch.caption ?? ""}\0${ch.order}\0${ch.content}`;
}

/** Last acknowledged server save for this chapter. Missing tokens count as 0. */
export function chapterSaveRev(ch: Chapter): number {
  if (typeof ch.rev === "number" && Number.isFinite(ch.rev)) return Math.floor(ch.rev);
  if (typeof ch.baseRev === "number" && Number.isFinite(ch.baseRev)) return Math.floor(ch.baseRev);
  const baseRev = ch.metadata?.syncBase?.rev;
  if (typeof baseRev === "number" && Number.isFinite(baseRev)) return Math.floor(baseRev);
  return 0;
}

function chapterDiffersFromSyncBase(ch: Chapter): boolean {
  const base = ch.metadata?.syncBase;
  if (!base?.content) return true;
  if ((base.title ?? ch.title) !== ch.title) return true;
  return serializeChapterContent(ch.content) !== serializeChapterContent(base.content);
}

/** True when this device has author work that has not been acknowledged as the library copy. */
export function bookHasUnpushedEdits(book: Book): boolean {
  return book.chapters.some((ch) => chapterDiffersFromSyncBase(ch));
}

function commentsFingerprint(comments: ChapterComment[]): string {
  return comments
    .map(
      (c) =>
        `${c.id}\0${c.text}\0${c.resolved ?? false}\0${c.createdAt}\0${c.authorUserId ?? ""}\0${
          c.createdBy ?? ""
        }\0${c.targetText ?? ""}\0${c.isStale ?? false}\0${c.parentCommentId ?? ""}\0${c.blockId ?? ""}`
    )
    .join("|");
}

function isCloudOwnedComment(comment: ChapterComment): boolean {
  return comment.authorUserId != null || comment.createdBy === "ai";
}

/**
 * Pull web/beta and Story Craft comments from the library without discarding local-only desktop notes.
 * Mirrors `mergeChapterCommentsForSync` in `@repo/main/database` (push direction).
 *
 * When `preferLocalFields` is true (local chapter is newer or tied), keep local text/resolved/etc.
 * for overlapping ids and only append remote-only cloud comments. This prevents a stale pull from
 * undoing a just-resolved comment before the push lands.
 */
export function mergeChapterCommentsForPull(
  local: ChapterComment[],
  remote: ChapterComment[],
  options?: { preferLocalFields?: boolean }
): ChapterComment[] {
  const preferLocalFields = options?.preferLocalFields ?? false;
  const localById = new Map(local.map((c) => [c.id, c]));
  const merged: ChapterComment[] = [...local];

  for (const remoteComment of remote) {
    const existing = localById.get(remoteComment.id);
    if (existing) {
      if (isCloudOwnedComment(remoteComment) && !preferLocalFields) {
        const idx = merged.findIndex((c) => c.id === remoteComment.id);
        if (idx >= 0) {
          merged[idx] = {
            ...existing,
            text: remoteComment.text,
            type: remoteComment.type ?? existing.type,
            resolved: remoteComment.resolved,
            createdBy: remoteComment.createdBy ?? existing.createdBy,
            targetText: remoteComment.targetText ?? existing.targetText,
            isStale: remoteComment.isStale ?? existing.isStale,
            authorUserId: remoteComment.authorUserId ?? existing.authorUserId,
            authorDisplayName: remoteComment.authorDisplayName ?? existing.authorDisplayName,
            parentCommentId: remoteComment.parentCommentId ?? existing.parentCommentId,
            blockId: remoteComment.blockId ?? existing.blockId,
            anchor: remoteComment.anchor ?? existing.anchor,
            createdAt: remoteComment.createdAt,
          };
        }
      }
      continue;
    }
    if (isCloudOwnedComment(remoteComment)) {
      merged.push(remoteComment);
      localById.set(remoteComment.id, remoteComment);
    }
  }

  return merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

function parseChapterDoc(content: unknown): ChapterDocContent {
  return toChapterDoc(content).doc as ChapterDocContent;
}

function excerptOf(content: unknown, max = 160): string {
  try {
    const text = (toChapterDoc(content)
      .doc.content?.map((n) => {
        const walk = (x: unknown): string => {
          if (!x || typeof x !== "object") return "";
          const o = x as { text?: string; content?: unknown[] };
          if (typeof o.text === "string") return o.text;
          return (o.content ?? []).map(walk).join("");
        };
        return walk(n);
      })
      .join(" ")
      .replace(/\s+/g, " ")
      .trim()) ?? "";
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
  } catch {
    return "";
  }
}

/**
 * Ensure every chapter has a syncBase snapshot so the next divergent sync can 3-way merge
 * instead of falling back to last-write-wins.
 */
export function seedChapterSyncBases(book: Book): { book: Book; seeded: boolean } {
  let seeded = false;
  const chapters = book.chapters.map((ch) => {
    if (ch.metadata?.syncBase?.content) return ch;
    seeded = true;
    const rev =
      typeof ch.baseRev === "number"
        ? ch.baseRev
        : typeof ch.rev === "number"
          ? ch.rev
          : 0;
    return {
      ...ch,
      metadata: {
        ...ch.metadata,
        syncBase: {
          rev,
          title: ch.title,
          content: serializeChapterContent(ch.content),
        },
      },
    };
  });
  if (!seeded) return { book, seeded: false };
  return { book: { ...book, chapters }, seeded: true };
}

/**
 * Merge one chapter: 3-way when both sides diverge from syncBase; otherwise newer
 * `updatedAt` wins (including when there is no stored base — load must take the
 * newest library/local body). Returns pendingConflict when overlapping edits
 * cannot auto-merge.
 */
export function mergeChapterThreeWay(
  local: Chapter,
  remote: Chapter
): { chapter: Chapter; conflict: PendingConflict } {
  const remoteMs = chapterTimeMs(remote.updatedAt);
  const localMs = chapterTimeMs(local.updatedAt);
  const remoteNewer = remoteMs > localMs;
  const mergedComments = mergeChapterCommentsForPull(local.comments, remote.comments, {
    preferLocalFields: !remoteNewer,
  });

  const localBody = chapterBodyFingerprint(local);
  const remoteBody = chapterBodyFingerprint(remote);
  if (localBody === remoteBody) {
    return {
      chapter: {
        ...local,
        title: remoteNewer ? remote.title : local.title,
        content: local.content,
        order: remoteNewer ? remote.order : local.order,
        kind: (remoteNewer ? remote.kind : local.kind) ?? local.kind ?? "chapter",
        chapterNumber:
          (remoteNewer ? remote.chapterNumber : local.chapterNumber) ?? local.chapterNumber ?? null,
        updatedAt: remoteNewer ? remote.updatedAt : local.updatedAt,
        rev: remote.rev ?? local.rev,
        baseRev: remote.rev ?? local.baseRev ?? local.rev,
        comments: mergedComments,
        variations: mergeChapterVariations(local.variations, remote.variations),
        metadata: {
          ...local.metadata,
          syncBase: {
            rev: remote.rev ?? local.rev ?? 0,
            title: remoteNewer ? remote.title : local.title,
            content: serializeChapterContent(local.content),
          },
        },
      },
      conflict: null,
    };
  }

  const syncBase = local.metadata?.syncBase;
  // No 3-way base: server rev is the save counter. A recovered snapshot with a
  // fresh updatedAt must not beat later web saves. Equal rev keeps local.
  if (!syncBase?.content) {
    const winner = chapterSaveRev(remote) > chapterSaveRev(local) ? remote : local;
    return {
      chapter: {
        ...local,
        title: winner.title,
        content: winner.content,
        order: remoteNewer ? remote.order : local.order,
        kind: winner.kind ?? local.kind ?? remote.kind ?? "chapter",
        chapterNumber: winner.chapterNumber ?? local.chapterNumber ?? remote.chapterNumber ?? null,
        updatedAt: winner.updatedAt,
        rev: remote.rev ?? local.rev,
        baseRev: remote.rev ?? local.baseRev ?? local.rev,
        comments: mergedComments,
        variations: mergeChapterVariations(local.variations, remote.variations),
        tabData: local.tabData,
        metadata: {
          ...local.metadata,
          syncBase: {
            rev: remote.rev ?? local.rev ?? 0,
            title: winner.title,
            content: serializeChapterContent(winner.content),
          },
        },
        notes: local.notes,
      },
      conflict: null,
    };
  }

  const baseContent = parseChapterDoc(syncBase.content);
  const baseTitle = syncBase.title ?? (remoteNewer ? local.title : remote.title);

  const merge = threeWayChapterMerge({
    base: baseContent,
    mine: parseChapterDoc(local.content),
    theirs: parseChapterDoc(remote.content),
    baseTitle,
    mineTitle: local.title,
    theirsTitle: remote.title,
  });

  if (merge.conflicts.length === 0) {
    const mergedContent = serializeChapterContent(merge.merged);
    return {
      chapter: {
        ...local,
        title: merge.mergedTitle,
        content: mergedContent,
        order: remoteNewer ? remote.order : local.order,
        kind: local.kind ?? remote.kind ?? "chapter",
        chapterNumber: local.chapterNumber ?? remote.chapterNumber ?? null,
        updatedAt: new Date(
          Math.max(localMs, remoteMs, Date.now())
        ).toISOString(),
        rev: remote.rev ?? local.rev,
        baseRev: remote.rev ?? local.baseRev,
        comments: mergedComments,
        variations: mergeChapterVariations(local.variations, remote.variations),
        metadata: {
          ...local.metadata,
          syncBase: {
            rev: remote.rev ?? local.rev ?? 0,
            title: merge.mergedTitle,
            content: mergedContent,
          },
        },
      },
      conflict: null,
    };
  }

  // Keep local body in the book; park conflict for UI. Prefer remote rev as next base.
  return {
    chapter: {
      ...local,
      comments: mergedComments,
      variations: mergeChapterVariations(local.variations, remote.variations),
      rev: remote.rev ?? local.rev,
      baseRev: remote.rev ?? local.baseRev,
    },
    conflict: {
      chapterId: local.id,
      chapterTitle: local.title || remote.title,
      localExcerpt: excerptOf(local.content),
      remoteExcerpt: excerptOf(remote.content),
      mergeJson: JSON.stringify(merge),
    },
  };
}

/** Prefer local on equal timestamps (editor may not bump chapter.updatedAt on every keystroke). */
function pickNewerChapter(local: Chapter, remote: Chapter): Chapter {
  return mergeChapterThreeWay(local, remote).chapter;
}

/**
 * Auto-resolve parked conflicts without discarding divergent local prose.
 * - Only remote changed → theirs
 * - Only local changed → mine
 * - Both changed → mine (never silent remote wipe)
 */
export function seamlessConflictResolutions(
  conflicts: ThreeWayMergeResult["conflicts"]
): ChapterConflictResolution[] {
  return conflicts.map((h) => {
    const base = h.baseText ?? "";
    const mine = h.mineText ?? "";
    const theirs = h.theirsText ?? "";
    if (mine === base && theirs !== base) {
      return { hunkId: h.id, choice: "theirs" as const };
    }
    return { hunkId: h.id, choice: "mine" as const };
  });
}

export function applyPendingConflictResolutions(
  book: Book,
  chapterId: string,
  resolutions: ChapterConflictResolution[]
): Book {
  const pending = book.phase2?.pendingConflict;
  if (!pending || pending.chapterId !== chapterId || !pending.mergeJson) return book;
  let merge: ThreeWayMergeResult;
  try {
    merge = JSON.parse(pending.mergeJson) as ThreeWayMergeResult;
  } catch {
    return book;
  }
  const doc = applyConflictResolutions(merge, resolutions);
  const content = serializeChapterContent(doc);
  return {
    ...book,
    chapters: book.chapters.map((ch) =>
      ch.id === chapterId
        ? {
            ...ch,
            title: merge.mergedTitle || ch.title,
            content,
            updatedAt: new Date().toISOString(),
            metadata: {
              ...ch.metadata,
              syncBase: {
                rev: ch.baseRev ?? ch.rev ?? 0,
                title: merge.mergedTitle || ch.title,
                content,
              },
            },
          }
        : ch
    ),
    phase2: {
      ...book.phase2!,
      pendingConflict: null,
    },
  };
}

export function mergeLocalAndRemoteChaptersByNewest(
  localBook: Book,
  remoteBook: Book,
  lastSyncAt: Date | null
): { book: Book; didChange: boolean; pendingConflict: PendingConflict } {
  const localById = new Map(localBook.chapters.map((c) => [c.id, c]));
  const remoteById = new Map(remoteBook.chapters.map((c) => [c.id, c]));

  const merged: Chapter[] = [];
  let pendingConflict: PendingConflict = localBook.phase2?.pendingConflict ?? null;
  for (const [id, loc] of localById) {
    const remoteCh = remoteById.get(id);
    if (remoteCh) {
      const result = mergeChapterThreeWay(loc, remoteCh);
      merged.push(result.chapter);
      if (result.conflict && !pendingConflict) {
        pendingConflict = result.conflict;
      }
    } else if (!lastSyncAt || chapterTimeMs(loc.createdAt) >= lastSyncAt.getTime()) {
      merged.push(loc);
    }
  }

  for (const [id, remoteCh] of remoteById) {
    if (localById.has(id)) continue;
    // First sync: take every remote-only chapter. Later syncs: only chapters
    // created after lastSyncAt (new on web). Older remote-only ids were deleted locally.
    if (!lastSyncAt || chapterTimeMs(remoteCh.createdAt) >= lastSyncAt.getTime()) {
      merged.push(remoteCh);
    }
  }

  merged.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  const renumbered = withSpineOrderAndNumbers(merged);

  const bookUpdatedMs = Math.max(
    chapterTimeMs(localBook.updatedAt),
    chapterTimeMs(remoteBook.updatedAt)
  );

  const nextBook: Book = {
    ...localBook,
    title: remoteBook.title || localBook.title,
    author: remoteBook.author || localBook.author,
    description: remoteBook.description || localBook.description,
    chapters: renumbered,
    updatedAt: new Date(bookUpdatedMs).toISOString(),
    phase2: {
      snapshots: localBook.phase2?.snapshots ?? [],
      connectorStates: localBook.phase2?.connectorStates ?? {
        googleDrive: false,
        oneDrive: false,
        dropbox: false,
      },
      lastPreflight: localBook.phase2?.lastPreflight ?? null,
      pendingConflict,
    },
  };

  let didChange = renumbered.length !== localBook.chapters.length;
  if (!didChange) {
    for (const ch of renumbered) {
      const prev = localById.get(ch.id);
      if (
        !prev ||
        chapterBodyFingerprint(prev) !== chapterBodyFingerprint(ch) ||
        commentsFingerprint(prev.comments) !== commentsFingerprint(ch.comments)
      ) {
        didChange = true;
        break;
      }
    }
  }
  if (pendingConflict && !localBook.phase2?.pendingConflict) {
    didChange = true;
  }

  return { book: nextBook, didChange, pendingConflict };
}

export function compareBookTimestamps(
  localBook: Book,
  remoteUpdatedAt: string | null
): "remote_newer" | "local_newer" | "same" | "no_remote" {
  if (!remoteUpdatedAt) return "no_remote";
  const localMs = chapterTimeMs(localBook.updatedAt);
  const remoteMs = chapterTimeMs(remoteUpdatedAt);
  if (remoteMs === localMs) return "same";
  return remoteMs > localMs ? "remote_newer" : "local_newer";
}

/** Replace local manuscript with the remote/library copy (discards unsaved local chapter bodies). */
function adoptRemoteChapterBody(local: Chapter, remote: Chapter): Chapter {
  const rev = chapterSaveRev(remote);
  return {
    ...local,
    title: remote.title,
    content: remote.content,
    order: remote.order,
    kind: remote.kind ?? local.kind ?? "chapter",
    chapterNumber: remote.chapterNumber ?? local.chapterNumber ?? null,
    updatedAt: remote.updatedAt,
    rev: remote.rev ?? rev,
    baseRev: remote.rev ?? rev,
    comments: mergeChapterCommentsForPull(local.comments, remote.comments, {
      preferLocalFields: false,
    }),
    variations: mergeChapterVariations(local.variations, remote.variations),
    metadata: {
      ...local.metadata,
      syncBase: {
        rev,
        title: remote.title,
        content: serializeChapterContent(remote.content),
      },
    },
  };
}

/**
 * Open/recover: the document on this device wins when it has unpushed author
 * work. Library bodies are adopted only for clean replicas (local matches
 * syncBase) that are behind the server rev.
 */
export function applyRemoteChaptersOnLoad(
  localBook: Book,
  remoteBook: Book
): { book: Book; didChange: boolean } {
  const localById = new Map(localBook.chapters.map((c) => [c.id, c]));
  const remoteById = new Map(remoteBook.chapters.map((c) => [c.id, c]));
  const merged: Chapter[] = [];

  for (const remoteCh of [...remoteBook.chapters].sort(
    (a, b) => a.order - b.order || a.id.localeCompare(b.id)
  )) {
    const loc = localById.get(remoteCh.id);
    if (!loc) {
      merged.push(remoteCh);
      continue;
    }
    const localRev = chapterSaveRev(loc);
    const remoteRev = chapterSaveRev(remoteCh);
    if (chapterDiffersFromSyncBase(loc)) {
      merged.push(loc);
      continue;
    }
    if (remoteRev > localRev) {
      merged.push(adoptRemoteChapterBody(loc, remoteCh));
    } else {
      merged.push(loc);
    }
  }

  for (const loc of localBook.chapters) {
    if (!remoteById.has(loc.id)) merged.push(loc);
  }

  const renumbered = withSpineOrderAndNumbers(
    merged.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
  );
  let didChange = renumbered.length !== localBook.chapters.length;
  if (!didChange) {
    for (const ch of renumbered) {
      const prev = localById.get(ch.id);
      if (!prev || chapterBodyFingerprint(prev) !== chapterBodyFingerprint(ch)) {
        didChange = true;
        break;
      }
    }
  }

  return {
    book: {
      ...localBook,
      title: remoteBook.title || localBook.title,
      author: remoteBook.author || localBook.author,
      description: remoteBook.description || localBook.description,
      chapters: renumbered,
      updatedAt: remoteBook.updatedAt || localBook.updatedAt,
    },
    didChange,
  };
}

export function replaceLocalWithRemoteBook(localBook: Book, remoteBook: Book): Book {
  const localById = new Map(localBook.chapters.map((c) => [c.id, c]));
  const chapters = withSpineOrderAndNumbers(
    [...remoteBook.chapters]
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
      .map((remoteCh) => {
        const local = localById.get(remoteCh.id);
        const rev =
          typeof remoteCh.rev === "number"
            ? remoteCh.rev
            : typeof remoteCh.baseRev === "number"
              ? remoteCh.baseRev
              : 0;
        const remoteMeta = remoteCh.metadata ?? local?.metadata;
        const syncBase =
          remoteMeta?.syncBase?.content
            ? remoteMeta.syncBase
            : {
                rev,
                title: remoteCh.title,
                content: serializeChapterContent(remoteCh.content),
              };
        return {
          ...remoteCh,
          kind: remoteCh.kind ?? "chapter",
          tabData: local?.tabData ?? remoteCh.tabData,
          metadata: {
            ...(remoteMeta ?? {
              status: "draft" as const,
              tags: [],
              keywords: [],
              label: "",
              customFields: {},
            }),
            // Prefer a fresh base from the remote body so the next edit can 3-way merge.
            syncBase,
          },
          variations: mergeChapterVariations(local?.variations, remoteCh.variations),
          notes: local?.notes ?? remoteCh.notes,
          baseRev: rev,
          rev: typeof remoteCh.rev === "number" ? remoteCh.rev : rev,
        };
      })
  );

  const localMeta = (localBook.metadata ?? {}) as Record<string, unknown>;
  const remoteMeta = (remoteBook.metadata ?? {}) as Record<string, unknown>;

  return {
    ...localBook,
    title: remoteBook.title,
    author: remoteBook.author,
    description: remoteBook.description,
    metadata: {
      ...remoteMeta,
      ...localMeta,
      desktopAssistant: localMeta.desktopAssistant ?? remoteMeta.desktopAssistant,
    },
    chapters,
    updatedAt: remoteBook.updatedAt,
  };
}

export function hasRemoteChapterNewerThanLocal(
  localBook: Book,
  remoteTimestamps: ChapterTimestamps
): boolean {
  for (const chapter of localBook.chapters) {
    const remoteIso = remoteTimestamps[chapter.id];
    if (remoteIso && chapterTimeMs(remoteIso) > chapterTimeMs(chapter.updatedAt)) {
      return true;
    }
  }
  return false;
}

export type AutoSyncOutcome = {
  book: Book;
  merged: boolean;
  /** True when syncBase (or other metadata) was written without a body merge. */
  seeded?: boolean;
  pushed: boolean;
  error?: string;
};

const LAST_SYNC_PREFIX = "jw_desktop_v2_last_sync_at_";

export function getLastSyncAt(bookId: string): Date | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(`${LAST_SYNC_PREFIX}${bookId}`);
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

export function setLastSyncAt(bookId: string, at: Date): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(`${LAST_SYNC_PREFIX}${bookId}`, at.toISOString());
}

function parseRevConflicts(
  failed: Array<{ message: string }>
): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of failed) {
    const match = /^CHAPTER_REV_CONFLICT:([^:]+):(\d+)$/.exec(item.message);
    if (!match) continue;
    map.set(match[1]!, Number(match[2]));
  }
  return map;
}

/** Stamp local chapters with known server revs after a successful author push.
 * If the server did not return revs, drop baseRev (next push is last-write-wins)
 * and refresh syncBase content so a later pull can 3-way merge from this snapshot.
 */
export function applyChapterRevsAfterPush(
  book: Book,
  chapterRevs?: Record<string, number> | null
): Book {
  return {
    ...book,
    chapters: book.chapters.map((ch) => {
      const raw = chapterRevs?.[ch.id];
      const nextRev =
        typeof raw === "number" && Number.isFinite(raw) ? Math.floor(raw) : undefined;
      return {
        ...ch,
        ...(nextRev != null ? { rev: nextRev, baseRev: nextRev } : { baseRev: undefined }),
        metadata: {
          ...ch.metadata,
          syncBase: {
            rev: nextRev ?? ch.metadata?.syncBase?.rev ?? 0,
            title: ch.title,
            content: serializeChapterContent(ch.content),
          },
        },
      };
    }),
  };
}

/** After a guarded push, server rev is baseRev+1 — keep local base in lockstep or every later push conflicts. */
export function advanceChapterSyncRevsAfterPush(book: Book): Book {
  return {
    ...book,
    chapters: book.chapters.map((ch) => {
      const prev =
        typeof ch.baseRev === "number"
          ? ch.baseRev
          : typeof ch.metadata?.syncBase?.rev === "number"
            ? ch.metadata.syncBase.rev
            : typeof ch.rev === "number"
              ? ch.rev
              : 0;
      const nextRev = prev + 1;
      // Critical: base content must match what we just pushed. Leaving a stale
      // syncBase makes the next pull look like "both sides edited" and can wipe local.
      return {
        ...ch,
        rev: nextRev,
        baseRev: nextRev,
        metadata: {
          ...ch.metadata,
          syncBase: {
            rev: nextRev,
            title: ch.title,
            content: serializeChapterContent(ch.content),
          },
        },
      };
    }),
  };
}

/** Drop concurrency tokens so a retry is last-write-wins on older servers. */
export function stripChapterBaseRevs(book: Book): Book {
  return {
    ...book,
    chapters: book.chapters.map((ch) => {
      const { baseRev: _baseRev, ...rest } = ch;
      return rest;
    }),
  };
}

function applyServerRevsFromConflicts(book: Book, revs: Map<string, number>): Book {
  if (revs.size === 0) return book;
  return {
    ...book,
    chapters: book.chapters.map((ch) => {
      const serverRev = revs.get(ch.id);
      if (serverRev == null) return ch;
      return {
        ...ch,
        rev: serverRev,
        baseRev: serverRev,
        metadata: {
          ...ch.metadata,
          syncBase: {
            rev: serverRev,
            title: ch.title,
            content: serializeChapterContent(ch.content),
          },
        },
      };
    }),
  };
}

export function summarizePushFailures(
  failed: Array<{ title: string; message: string }>
): string {
  const n = failed.length;
  const conflicts = failed.filter((f) => f.message.startsWith("CHAPTER_REV_CONFLICT")).length;
  if (conflicts === n && n > 0) {
    return `Your writing is saved. Chapter order didn't sync (${n}) — it retries on the next save.`;
  }
  const sample = (
    failed.find((f) => !f.message.startsWith("CHAPTER_REV_CONFLICT"))?.message ?? "unknown error"
  ).slice(0, 120);
  return `Your writing is saved. Chapter order didn't sync (${n}): ${sample}`;
}

/** An empty TipTap document — the placeholder for a body this push will not write. */
const EMPTY_CHAPTER_DOC_JSON = JSON.stringify({
  type: "doc",
  content: [{ type: "paragraph" }],
});

/**
 * Chapter prose belongs to the per-chapter outbox, so the whole-book push sends
 * structure with the bodies removed. The server refuses to overwrite an existing
 * body anyway; dropping them here also keeps the payload small.
 *
 * `baseRev` goes with the body. It is a token asserting "I am editing revision
 * N of this prose", and the outbox bumps the server's revision on every push,
 * so a structure payload that kept it would be stale from the moment the outbox
 * delivered anything — permanently, since this push never adopts server revs.
 */
export function withoutChapterBodies(book: Book): Book {
  return {
    ...book,
    chapters: book.chapters.map((ch) => {
      const { baseRev: _baseRev, ...rest } = ch;
      return { ...rest, content: EMPTY_CHAPTER_DOC_JSON };
    }),
  };
}

/**
 * Push structure only.
 *
 * Deliberately returns the caller's book untouched: adopting revs or refreshing
 * `syncBase` here would claim the server has a body it never received, and a
 * later pull would then feel free to discard whatever the outbox still holds.
 */
export async function pushBookStructureOnly(
  book: Book,
  adapter: DesktopV2SyncAdapter
): Promise<{ book: Book; pushResult: SyncPushResult }> {
  const pushResult = await adapter.pushBook(withoutChapterBodies(book));
  return { book, pushResult };
}

export async function pushBookWithRevRetry(
  book: Book,
  adapter: DesktopV2SyncAdapter
): Promise<{ book: Book; pushResult: SyncPushResult }> {
  let working = book;
  let pushResult = await adapter.pushBook(working);
  let failed = pushResult.failedChapters ?? [];
  if (failed.length === 0) {
    return { book: applyChapterRevsAfterPush(working, pushResult.chapterRevs), pushResult };
  }

  const conflictRevs = parseRevConflicts(failed);
  if (conflictRevs.size === 0) {
    return { book: working, pushResult };
  }

  // Author save: the open document wins. Adopt the library's current rev tokens
  // and write this body. History already snapshotted the previous library copy.
  const hasServerRevs =
    Boolean(pushResult.chapterRevs) && Object.keys(pushResult.chapterRevs ?? {}).length > 0;
  working = hasServerRevs
    ? applyChapterRevsAfterPush(working, pushResult.chapterRevs)
    : applyServerRevsFromConflicts(working, conflictRevs);
  pushResult = await adapter.pushBook(working);
  failed = pushResult.failedChapters ?? [];
  if (failed.length === 0) {
    return { book: applyChapterRevsAfterPush(working, pushResult.chapterRevs), pushResult };
  }

  const stripped = stripChapterBaseRevs(working);
  const strippedResult = await adapter.pushBook(stripped);
  if ((strippedResult.failedChapters?.length ?? 0) === 0) {
    return {
      book: applyChapterRevsAfterPush(stripped, strippedResult.chapterRevs),
      pushResult: strippedResult,
    };
  }
  return { book: working, pushResult: strippedResult };
}

/**
 * Pull (when remote is newer), merge by chapter `updatedAt`, then push merged book to the database.
 */
export async function runBidirectionalSync(
  localBook: Book,
  adapter: DesktopV2SyncAdapter
): Promise<AutoSyncOutcome> {
  const bookId = typeof localBook.id === "string" ? localBook.id.trim() : "";
  if (!bookId) {
    return { book: localBook, merged: false, pushed: false, error: "Book has no id for cloud sync" };
  }

  try {
    const exists = await adapter.bookExists(bookId);
    let working = localBook;
    let merged = false;
    const lastSyncAt = getLastSyncAt(bookId);

    if (exists) {
      const remoteBook = await adapter.loadRemoteBook(bookId);
      if (remoteBook) {
        const mergeResult = mergeLocalAndRemoteChaptersByNewest(working, remoteBook, lastSyncAt);
        working = mergeResult.book;
        merged = mergeResult.didChange;
      }
    }

    const seededResult = seedChapterSyncBases(working);
    working = seededResult.book;

    const { book: pushedBook, pushResult } = await pushBookStructureOnly(working, adapter);
    working = pushedBook;
    const failed = pushResult.failedChapters?.length ?? 0;
    if (failed > 0) {
      return {
        book: working,
        merged,
        seeded: seededResult.seeded,
        pushed: false,
        error: summarizePushFailures(pushResult.failedChapters ?? []),
      };
    }

    setLastSyncAt(bookId, new Date());
    return { book: working, merged, seeded: seededResult.seeded, pushed: true };
  } catch (e) {
    return {
      book: localBook,
      merged: false,
      pushed: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}
